import { Extension, InputRule, textInputRule } from "@tiptap/core";

/**
 * Smart-typing input rules. Conservative defaults for an academic editor:
 *  - en dash `--` → `–`
 *  - em dash `---` → `—`  (must be matched before `--`)
 *  - ellipsis `...` → `…`
 *  - fractions `1/2` `1/4` `3/4` → `½ ¼ ¾`
 *  - copyright `(c)` `(C)` → `©`
 *  - registered `(r)` `(R)` → `®`
 *  - trademark `(tm)` `(TM)` → `™`
 *  - smart double quotes: `"` typed at start-of-word → `“`, otherwise → `”`
 *  - smart single quotes: `'` typed at start-of-word → `‘`, otherwise → `’`
 *
 * Smart quotes only fire on direct keystrokes — pasted text passes through
 * unchanged, so LaTeX snippets like `` ``foo'' `` survive a paste intact.
 * The C# LmlConversionService also runs a safety-net regex on export that
 * pairs any remaining straight `"…"` into ``…''.
 */
export const Typography = Extension.create({
  name: "typography",

  addInputRules() {
    return [
      textInputRule({ find: /---$/, replace: "—" }),
      textInputRule({ find: /(?<!-)--$/, replace: "–" }),
      textInputRule({ find: /\.\.\.$/, replace: "…" }),
      textInputRule({ find: /\(c\)$/i, replace: "©" }),
      textInputRule({ find: /\(r\)$/i, replace: "®" }),
      textInputRule({ find: /\(tm\)$/i, replace: "™" }),
      textInputRule({ find: /1\/2$/, replace: "½" }),
      textInputRule({ find: /1\/4$/, replace: "¼" }),
      textInputRule({ find: /3\/4$/, replace: "¾" }),
      // Smart double-quote: opening after start/whitespace/open-bracket,
      // closing everywhere else. textInputRule is greedy so we use a
      // raw InputRule and select the variant based on the captured prefix.
      new InputRule({
        find: /(^|[\s([{<])"$/,
        handler: ({ state, range, match }) => {
          const prefix = match[1] ?? "";
          state.tr.insertText(`${prefix}“`, range.from, range.to);
        },
      }),
      new InputRule({
        find: /"$/,
        handler: ({ state, range }) => {
          state.tr.insertText("”", range.from, range.to);
        },
      }),
      // Smart single-quote / apostrophe: opening only after a clear
      // start-of-word boundary; otherwise closing (covers apostrophes
      // in contractions like "don't" → "don’t").
      new InputRule({
        find: /(^|[\s([{<])'$/,
        handler: ({ state, range, match }) => {
          const prefix = match[1] ?? "";
          state.tr.insertText(`${prefix}‘`, range.from, range.to);
        },
      }),
      new InputRule({
        find: /'$/,
        handler: ({ state, range }) => {
          state.tr.insertText("’", range.from, range.to);
        },
      }),
    ];
  },
});
